import { ExcelFileViewer } from "@/components/common/ExcelViewer/ExcelFileViewer"
import { Typography } from "@/components/common/Typography/Typography"
import type { ExcelEditorHandle, LoadEvent } from "@/components/ExcelEditor"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { WorkbookInstance } from "@fortune-sheet/react"
import { MutableRefObject, type Ref } from "react"

interface ExcelPreviewPanelProps {
  excelFileUrl: string | undefined
  isLoading: boolean
  isError: boolean
  workbookRef: MutableRefObject<WorkbookInstance | null>
  /** The new editor's handle, for a screen that drives it (EXCEL_EDITOR_V2). */
  editorRef?: Ref<ExcelEditorHandle>
  /** Rows grow to fit their text, written answers included (EXCEL_EDITOR_V2). */
  autoRowHeight?: boolean
  /** The new editor has loaded the workbook (EXCEL_EDITOR_V2). */
  onLoad?: (event: LoadEvent) => void
}

export const ExcelPreviewPanel: React.FC<ExcelPreviewPanelProps> = ({
  excelFileUrl,
  isLoading,
  isError,
  workbookRef,
  editorRef,
  autoRowHeight,
  onLoad,
}) => {
  const { translate } = useTranslation()

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-grey3">
        <div className="text-center">
          <Typography as="h2" variant="titleM" className="pb-M">
            {translate("QUESTIONNAIRE_ANSWERS/PREVIEW_TITLE")}
          </Typography>
          <Typography as="p" variant="textS" className="text-grey5">
            {translate("QUESTIONNAIRE_ANSWERS/PREVIEW_DESCRIPTION")}
          </Typography>
        </div>
      </div>
    )
  }

  if (excelFileUrl) {
    return (
      <div className="flex h-full w-full flex-col overflow-hidden">
        <div className="w-full flex-1 overflow-hidden">
          <ExcelFileViewer
            file={excelFileUrl}
            workbookRef={workbookRef}
            editorRef={editorRef}
            autoRowHeight={autoRowHeight}
            onLoad={onLoad}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full items-center justify-center bg-grey3">
      <div className="text-center">
        <Typography as="h2" variant="titleM" className="pb-M">
          {translate("QUESTIONNAIRE_ANSWERS/PREVIEW_TITLE")}
        </Typography>
        <Typography as="p" variant="textS" className="text-grey5">
          {isError
            ? translate("ERROR/SOMETHING_WENT_WRONG")
            : translate("QUESTIONNAIRE_ANSWERS/PREVIEW_DESCRIPTION")}
        </Typography>
      </div>
    </div>
  )
}
